// The private meeting notepad became a rich-text editor, so a note body is
// HTML. The column was NOT migrated: bodies written before that are plain text
// and stay plain text in the database until their author next saves them, so
// every consumer has to cope with both shapes — this is the one place that
// decides which is which. The backend has a mirror of these helpers in
// backend/src/lib/noteHtml.ts; keep the two in step.

// A body is treated as HTML the moment it carries a single tag. Plain text
// containing a stray '<' (someone writing "<10 members") has no closing tag
// shape and stays plain text.
const HTML_TAG = /<\/?[a-z][a-z0-9]*(?:\s[^>]*)?\/?>/i;

export function looksLikeHtml(body: string): boolean {
  return HTML_TAG.test(body);
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * A stored note body as HTML the editor can be seeded with.
 *
 * Legacy plain text is escaped (it is text, never markup) and wrapped one
 * paragraph per line, with blank lines kept as empty paragraphs so the note
 * still LOOKS the way its author left it — line breaks in a notepad are
 * deliberate. Nothing is written back to the server by this: the conversion
 * happens on load, and the HTML shape is only persisted if and when the author
 * edits the note.
 */
export function noteBodyToHtml(body: string): string {
  if (looksLikeHtml(body)) return body;
  if (body === '') return '';
  return body
    .split('\n')
    .map((line) => {
      const text = escapeHtml(line.replace(/\r$/, ''));
      return text === '' ? '<p></p>' : `<p>${text}</p>`;
    })
    .join('');
}

/** True when the document holds nothing anyone wrote — `<p></p>` included. */
export function isNoteHtmlEmpty(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').trim() === '';
}
